export const INPUT_VALIDATION_CODES = {
  invalidIbanFormat: 'INVALID_IBAN_FORMAT',
  invalidIbanChecksum: 'INVALID_IBAN_CHECKSUM',
  invalidEmail: 'INVALID_EMAIL',
  invalidAmount: 'INVALID_AMOUNT',
  invalidContactField: 'INVALID_CONTACT_FIELD',
  invalidDebitDay: 'INVALID_DEBIT_DAY',
  invalidDate: 'INVALID_DATE',
  invalidModalPayload: 'MODAL_PAYLOAD_INVALID',
} as const;

const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28,
  CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24, FI: 18,
  FR: 27, GB: 22, GI: 23, GR: 27, HR: 21, HU: 28,
  IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20,
  LV: 21, MC: 27, MT: 31, NL: 18, NO: 15, PL: 28,
  PT: 25, RO: 24, SE: 24, SI: 19, SK: 24, SM: 27,
  VA: 22,
};

export const DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS = 500;
const ECMASCRIPT_TRIM_WHITESPACE = /^[\u0009-\u000D\u0020\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]+|[\u0009-\u000D\u0020\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]+$/g;

export function normalizeIban(value: string): string {
  return value.trim().replace(/[\s-]+/g, '').toUpperCase();
}

export function isValidIban(value: string): boolean {
  return ibanValidationCode(value) === null;
}

export function ibanValidationCode(value: string): string | null {
  const candidate = normalizeIban(value);
  const country = candidate.slice(0, 2);

  if (
    !candidate
    || !/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(candidate)
    || IBAN_LENGTHS[country] !== candidate.length
  ) {
    return INPUT_VALIDATION_CODES.invalidIbanFormat;
  }

  return hasValidIbanChecksum(candidate) ? null : INPUT_VALIDATION_CODES.invalidIbanChecksum;
}

function hasValidIbanChecksum(candidate: string): boolean {
  const rearranged = candidate.slice(4) + candidate.slice(0, 4);
  let remainder = 0;

  for (const char of rearranged) {
    const valuePart = char >= 'A' && char <= 'Z'
      ? String(char.charCodeAt(0) - 55)
      : char;

    for (const digit of valuePart) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }

  return remainder === 1;
}

export function isValidEmail(value: string): boolean {
  const candidate = value.trim();
  if (!candidate || /\s/.test(candidate) || candidate.split('@').length !== 2) {
    return false;
  }

  const [localPart, domainPart] = candidate.split('@');
  const domain = domainPart.toLowerCase();

  return isValidEmailLocalPart(localPart) && isValidEmailDomain(domain);
}

export function parsePositiveMoneyAmount(value: string | number): number | null {
  const normalized = normalizeMoneyAmount(value);
  if (normalized === null) {
    return null;
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export function isValidPositiveMoneyAmount(value: string | number): boolean {
  return parsePositiveMoneyAmount(value) !== null;
}

export function isValidIsoDate(value: string): boolean {
  const candidate = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
    return false;
  }

  const [year, month, day] = candidate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export function isFutureOrTodayIsoDate(value: string): boolean {
  if (!isValidIsoDate(value)) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return date >= today;
}

export function isValidDebitDay(value: string): boolean {
  return /^\d{1,2}$/.test(value.trim())
    && Number(value) >= 1
    && Number(value) <= 28;
}

export function isValidContractDebitDay(value: string): boolean {
  return ['1', '15'].includes(value.trim());
}

export function isPlausiblePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 5
    && digits.length <= 20
    && /^[+()\d\s./-]+$/.test(value);
}

export function isValidDocumentUploadComment(value: string): boolean {
  const candidate = normalizeDocumentUploadComment(value);
  return documentUploadCommentCodePointLength(candidate) <= DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS
    && /[\p{L}\p{N}]+/u.test(candidate);
}

export function documentUploadCommentCodePointLength(value: string): number {
  return Array.from(normalizeDocumentUploadComment(value)).length;
}

export function isDocumentUploadCommentTooLong(value: string): boolean {
  return documentUploadCommentCodePointLength(value) > DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS;
}

export function normalizeDocumentUploadComment(value: string): string {
  return value.replace(ECMASCRIPT_TRIM_WHITESPACE, '');
}

function isValidEmailLocalPart(localPart: string): boolean {
  return localPart !== ''
    && localPart.length <= 64
    && !localPart.startsWith('.')
    && !localPart.endsWith('.')
    && !localPart.includes('..')
    && /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/.test(localPart);
}

function isValidEmailDomain(domain: string): boolean {
  if (
    domain === ''
    || domain.length > 255
    || domain.startsWith('.')
    || domain.startsWith('-')
    || domain.endsWith('.')
    || domain.endsWith('-')
    || domain.includes('..')
    || !/^[A-Za-z0-9.-]+$/.test(domain)
  ) {
    return false;
  }

  const labels = domain.split('.');
  if (labels.length < 2) {
    return false;
  }

  return labels.every((label) => label !== ''
    && !label.startsWith('-')
    && !label.endsWith('-')
    && /^[A-Za-z0-9-]+$/.test(label))
    && /^[A-Za-z]{2,}$/.test(labels[labels.length - 1]);
}

function normalizeMoneyAmount(value: string | number): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toFixed(2) : null;
  }

  let candidate = value.trim();
  if (!candidate || /[-−]/u.test(candidate)) {
    return null;
  }

  candidate = candidate.replace(/\s*(?:€|EUR|Euro)\s*$/iu, '').trim().replace(/\s+/g, '');
  if (!candidate || !/^\d+(?:[.,]\d+)*$/.test(candidate)) {
    return null;
  }

  const lastDot = candidate.lastIndexOf('.');
  const lastComma = candidate.lastIndexOf(',');

  if (lastDot !== -1 && lastComma !== -1) {
    candidate = lastComma > lastDot
      ? candidate.replace(/\./g, '').replace(',', '.')
      : candidate.replace(/,/g, '');

    return /^\d+(?:\.\d{1,2})?$/.test(candidate) ? candidate : null;
  }

  if (lastComma !== -1) {
    return normalizeSingleSeparatorAmount(candidate, ',');
  }

  if (lastDot !== -1) {
    return normalizeSingleSeparatorAmount(candidate, '.');
  }

  return /^\d+$/.test(candidate) ? `${candidate}.00` : null;
}

function normalizeSingleSeparatorAmount(candidate: string, separator: string): string | null {
  const parts = candidate.split(separator);

  if (parts.length > 2) {
    return parts.slice(1).every((group) => group.length === 3 && /^\d+$/.test(group))
      ? `${parts.join('')}.00`
      : null;
  }

  const [euros, fraction] = parts;
  if (!euros || !fraction || !/^\d+$/.test(euros) || !/^\d+$/.test(fraction)) {
    return null;
  }

  if (fraction.length <= 2) {
    return `${euros}.${fraction.padEnd(2, '0')}`;
  }

  if (fraction.length === 3) {
    return `${euros}${fraction}.00`;
  }

  return null;
}
